import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  ListGroup,
  Nav,
} from "react-bootstrap";
import axios from "axios";
import toast from "react-hot-toast";

/**
 * 4-step ABHA enrolment wizard (M1) — driven by the backend
 *   /api/abdm/abha/aadhaar/{otp,verify,suggest,address}
 *
 * Props
 * -----
 *   show              boolean       — modal visibility
 *   onHide            () => void    — close handler
 *   initialAadhaar    string?       — pre-fill Aadhaar from the patient form
 *   patientId         string?       — Mongo _id; if present, "Finish & Link" calls
 *                                     /api/abdm/abha/link/:patientId
 *   onComplete        (result) => void — fired with { abha_number, abha_address, txnId }
 *                                       when the wizard completes successfully
 */
const STEP = {
  AADHAAR: 0,
  OTP: 1,
  ADDRESS: 2,
  DONE: 3,
};

const METHOD = {
  AADHAAR: "aadhaar",
  DL: "dl",
};

function digitsOnly(s) {
  return String(s || "").replace(/\D/g, "");
}

function authHeader() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function abhaApi(path) {
  return `${process.env.REACT_APP_API_URL}/abdm/abha${path}`;
}

function extractError(err, fallback) {
  const upstream = err?.response?.data?.upstream;
  const upstreamDetails = Array.isArray(upstream?.details)
    ? upstream.details
        .map((d) => d?.message || d?.code || "")
        .filter(Boolean)
        .join("; ")
    : "";
  const val =
    upstreamDetails ||
    upstream?.message ||
    upstream?.error ||
    err?.response?.data?.message ||
    (err?.response?.data?.error && typeof err.response.data.error === "object"
      ? err.response.data.error.message || JSON.stringify(err.response.data.error)
      : err?.response?.data?.error) ||
    err?.message ||
    fallback;

  return typeof val === "string" ? val : JSON.stringify(val);
}

function formatDobForDl(input) {
  if (!input) return "";
  // HTML date input gives YYYY-MM-DD; ABDM DL flow expects DD-MM-YYYY.
  const m = String(input).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return input;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

export default function AbhaCreateWizard({
  show,
  onHide,
  initialAadhaar,
  initialMobile,
  patientId,
  onComplete,
}) {
  const [step, setStep] = useState(STEP.AADHAAR);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState(METHOD.AADHAAR);

  const [aadhaar, setAadhaar] = useState("");
  const [otp, setOtp] = useState("");
  const [mobile, setMobile] = useState("");
  const [dlNumber, setDlNumber] = useState("");
  const [dob, setDob] = useState("");

  const [txnId, setTxnId] = useState("");
  const [profile, setProfile] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [chosenAddress, setChosenAddress] = useState("");
  const [tokens, setTokens] = useState(null);

  useEffect(() => {
    if (show) {
      setStep(STEP.AADHAAR);
      setMethod(METHOD.AADHAAR);
      setAadhaar(digitsOnly(initialAadhaar));
      setOtp("");
      setMobile(digitsOnly(initialMobile));
      setDlNumber("");
      setDob("");
      setTxnId("");
      setProfile(null);
      setSuggested([]);
      setChosenAddress("");
      setTokens(null);
      setBusy(false);
    }
  }, [show, initialAadhaar, initialMobile]);

  const requestOtp = async () => {
    const a = digitsOnly(aadhaar);
    if (a.length !== 12) {
      toast.error("Aadhaar must be 12 digits.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        abhaApi("/aadhaar/otp"),
        { aadhaar: a },
        { headers: authHeader() },
      );
      setTxnId(data.txnId);
      setStep(STEP.OTP);
      toast.success(data.message || "OTP sent to Aadhaar-linked mobile.");
    } catch (e) {
      toast.error(extractError(e, "Failed to send OTP"));
    } finally {
      setBusy(false);
    }
  };

  const requestDlOtp = async () => {
    const m = digitsOnly(mobile);
    if (m.length !== 10) {
      toast.error("Enter a valid 10-digit mobile number.");
      return;
    }
    if (!dlNumber.trim()) {
      toast.error("Driving License number is required.");
      return;
    }
    if (!dob) {
      toast.error("Date of birth is required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        abhaApi("/dl/otp"),
        { mobile: m },
        { headers: authHeader() },
      );
      setTxnId(data.txnId);
      setStep(STEP.OTP);
      toast.success(data.message || "OTP sent to mobile.");
    } catch (e) {
      toast.error(extractError(e, "Failed to send OTP"));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length < 4) {
      toast.error("Enter the OTP you received.");
      return;
    }
    if (method === METHOD.AADHAAR) {
      const cleanMobile = digitsOnly(mobile);
      if (!cleanMobile || cleanMobile.length !== 10) {
        toast.error("Please enter a valid 10-digit mobile number for ABHA communication.");
        return;
      }
    }
    setBusy(true);
    try {
      let data;
      if (method === METHOD.AADHAAR) {
        const body = { txnId, otp: digitsOnly(otp), mobile: digitsOnly(mobile) };
        const resp = await axios.post(abhaApi("/aadhaar/verify"), body, {
          headers: authHeader(),
        });
        data = resp.data;
      } else {
        const resp = await axios.post(
          abhaApi("/dl/verify"),
          {
            txnId,
            otp: digitsOnly(otp),
            dlNumber: dlNumber.trim(),
            dob: formatDobForDl(dob),
          },
          { headers: authHeader() },
        );
        data = resp.data;
      }
      const abha = data.abha || {};
      const rawAddr = abha.phrAddress || abha.abhaAddress;
      const cleanAddr = Array.isArray(rawAddr) ? rawAddr[0] : rawAddr;
      setProfile({
        abhaNumber: abha.ABHANumber || abha.healthIdNumber,
        abhaAddress: typeof cleanAddr === "string" ? cleanAddr.trim() : "",
        firstName: abha.firstName,
        lastName: abha.lastName,
        name: abha.name,
        gender: abha.gender,
        yearOfBirth: abha.yearOfBirth,
        mobile: abha.mobile,
        isNew: data.isNew,
      });
      setTokens(data.tokens || null);
      toast.success(
        data.isNew ? "ABHA created. Pick an ABHA address." : "ABHA verified.",
      );

      try {
        const { data: sug } = await axios.post(
          abhaApi("/aadhaar/suggest"),
          { txnId },
          { headers: authHeader() },
        );
        setSuggested(sug.abhaAddressList || []);
      } catch (sugErr) {
        if (!(abha.phrAddress || abha.abhaAddress)) {
          toast.error(
            extractError(sugErr, "Could not load suggested ABHA addresses"),
          );
        }
      }

      setStep(STEP.ADDRESS);
    } catch (e) {
      toast.error(extractError(e, "OTP verification failed"));
    } finally {
      setBusy(false);
    }
  };

  const claimAddress = async (skip = false) => {
    setBusy(true);
    try {
      let abhaAddress = profile?.abhaAddress || "";
      if (Array.isArray(abhaAddress)) abhaAddress = abhaAddress[0];
      abhaAddress = typeof abhaAddress === "string" ? abhaAddress.trim() : "";
      if (!skip && chosenAddress) {
        const { data } = await axios.post(
          abhaApi("/aadhaar/address"),
          { txnId, abhaAddress: chosenAddress },
          { headers: authHeader() },
        );
        abhaAddress = data.abhaAddress || chosenAddress;
      }

      const result = {
        abha_number: digitsOnly(profile?.abhaNumber || ""),
        abha_address: abhaAddress || "",
        txnId,
        profile,
        tokens,
      };

      if (patientId) {
        try {
          await axios.post(
            abhaApi(`/link/${patientId}`),
            { txnId },
            { headers: authHeader() },
          );
          toast.success("ABHA linked to patient.");
        } catch (linkErr) {
          toast.error(extractError(linkErr, "Failed to link ABHA to patient"));
        }
      }

      onComplete?.(result);
      setStep(STEP.DONE);
    } catch (e) {
      toast.error(extractError(e, "Could not claim ABHA address"));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setBusy(false);
    onHide?.();
  };

  return (
    <Modal show={show} onHide={close} backdrop="static" size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          ABHA enrolment {profile?.isNew === false ? "(verify existing)" : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === STEP.AADHAAR && (
          <>
            <p className="text-muted">
              Enter the 12-digit Aadhaar of the patient. An OTP will be sent
              to the Aadhaar-linked mobile.
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Aadhaar number</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                maxLength={12}
                value={aadhaar}
                onChange={(e) => setAadhaar(digitsOnly(e.target.value))}
                placeholder="999999990019"
                autoFocus
              />
              <Form.Text muted>
                Sandbox test Aadhaar: <code>999999990019</code>, OTP{" "}
                <code>123456</code>.
              </Form.Text>
            </Form.Group>
          </>
        )}

        {step === STEP.OTP && (
          <>
            <Alert variant="info" className="mb-3">
              OTP sent. txnId: <code>{txnId}</code>
            </Alert>
            <Form.Group className="mb-3">
              <Form.Label>OTP</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                maxLength={8}
                value={otp}
                onChange={(e) => setOtp(digitsOnly(e.target.value))}
                placeholder="6-digit OTP"
                autoFocus
              />
            </Form.Group>
            {method === METHOD.AADHAAR && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Mobile number for ABHA communication
                </Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  value={mobile}
                  onChange={(e) => setMobile(digitsOnly(e.target.value))}
                  placeholder="10-digit mobile"
                />
              </Form.Group>
            )}
          </>
        )}

        {step === STEP.ADDRESS && (
          <>
            {profile && (
              <Alert variant="success" className="mb-3">
                <div>
                  <strong>ABHA created/verified.</strong>
                </div>
                <div>
                  ABHA #: <code>{profile.abhaNumber}</code>
                </div>
                {profile.abhaAddress && (
                  <div>
                    Existing address: <code>{profile.abhaAddress}</code>
                  </div>
                )}
                {profile.name && <div>Name: {profile.name}</div>}
                {profile.yearOfBirth && (
                  <div>Year of birth: {profile.yearOfBirth}</div>
                )}
              </Alert>
            )}

            {suggested.length > 0 ? (
              <>
                <Form.Label>Pick an ABHA address</Form.Label>
                <ListGroup className="mb-3">
                  {suggested.map((addr) => (
                    <ListGroup.Item
                      key={addr}
                      action
                      active={chosenAddress === addr}
                      onClick={() => setChosenAddress(addr)}
                    >
                      {addr}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            ) : (
              <Alert variant="secondary">
                No new addresses to claim — the existing ABHA address (if any)
                will be used.
              </Alert>
            )}
          </>
        )}

        {step === STEP.DONE && (
          <Alert variant="success">
            <strong>Done.</strong> ABHA captured for this patient. You can
            continue filling the patient form.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === STEP.AADHAAR && (
          <>
            <Button variant="secondary" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={requestOtp}
              disabled={busy}
            >
              {busy ? <Spinner animation="border" size="sm" /> : "Send OTP"}
            </Button>
          </>
        )}
        {step === STEP.OTP && (
          <>
            <Button
              variant="secondary"
              onClick={() => setStep(STEP.AADHAAR)}
              disabled={busy}
            >
              Back
            </Button>
            <Button variant="primary" onClick={verifyOtp} disabled={busy}>
              {busy ? <Spinner animation="border" size="sm" /> : "Verify OTP"}
            </Button>
          </>
        )}
        {step === STEP.ADDRESS && (
          <>
            <Button
              variant="outline-secondary"
              onClick={() => claimAddress(true)}
              disabled={busy}
            >
              Skip address claim
            </Button>
            <Button
              variant="primary"
              onClick={() => claimAddress(false)}
              disabled={busy || (!chosenAddress && !profile?.abhaAddress)}
            >
              {busy ? (
                <Spinner animation="border" size="sm" />
              ) : patientId ? (
                "Claim & link to patient"
              ) : (
                "Claim address"
              )}
            </Button>
          </>
        )}
        {step === STEP.DONE && (
          <Button variant="success" onClick={close}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
